import { DATE_FORMAT_MAP } from "../constants/index.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
dayjs.extend(utc);
dayjs.extend(customParseFormat);
/**
 * An array of string patterns for valid date formats in Day/Month/Year (DMY) structure.
 */
export const validDMYFormats = [
    "DD/MM/YYYY",
    "D/M/YYYY",
    "DD-MM-YYYY",
    "YYYY-MM-DD",
];
/**
 * Checks if a given value is a valid date string in DMY format.
 */
export const isValidDateValue = (value) => value != null &&
    value !== "" &&
    validDMYFormats.some((fmt) => dayjs(value, fmt, true).isValid());
/**
 * Normalises a given date value to a string in YYYY-MM-DD format.
 */
export const normaliseDateValue = (value) => {
    for (const fmt of validDMYFormats) {
        const parsed = dayjs(value, fmt, true);
        if (parsed.isValid())
            return parsed.format("YYYY-MM-DD");
    }
    return value;
};
/**
 * Gets a `Dayjs` object with a set time depending on the provided parameter values.
 * @param {Number} hours Number of hours
 * @param {Number} min Number of minutes
 * @param {Number} sec Number of seconds
 * @param {Number} ms Number of milliseconds
 * @param {Date | dayjs.Dayjs | string} date Date to use as a base, if not provided, current Date is used
 * @returns {dayjs.Dayjs} Dayjs object
 */
export const getDateWithCustomTime = (hours = 0, min = 0, sec = 0, ms = 0, date = null) => {
    const newDate = date ? dayjs(date) : dayjs();
    return newDate.hour(hours).minute(min).second(sec).millisecond(ms);
};
/**
 * Checks if a value is a valid hour (0-23).
 *
 * @param {unknown} value The value to check.
 * @returns {boolean} True if the value is a number between 0 and 23 (inclusive), otherwise false.
 */
export const isValidHour = (value) => {
    if (typeof value !== "number" || !Number.isInteger(value)) {
        return false;
    }
    return value >= 0 && value <= 23;
};
/**
 * Checks if a value is a valid minute (0-59).
 *
 * @param {unknown} value The value to check.
 * @returns {boolean} True if the value is a number between 0 and 59 (inclusive), otherwise false.
 */
export const isValidMinute = (value) => {
    if (typeof value !== "number" || !Number.isInteger(value)) {
        return false;
    }
    return value >= 0 && value <= 59;
};
export const getWeekDayLabel = (day, format = "dd") => {
    if (Number.isInteger(day) && day >= 0 && day <= 7) {
        // handle ISO Sunday (7) as dayjs Sunday (0)
        if (day === 7) {
            return dayjs().set("day", 0).format(format);
        }
        return dayjs().set("day", day).format(format);
    }
    return "";
};
/**
 * Converts a Day.js-compatible value to a UTC ISO 8601 date string in 'YYYY-MM-DD' format.
 * The function takes any value that Day.js can parse, shifts it to UTC, and extracts the date part.
 *
 * @param {dayjs.ConfigType} value - The value to convert. This can be a Day.js object, a native Date object, a number, or a string.
 * @returns {string} The formatted date string in 'YYYY-MM-DD' format (e.g., '2023-10-27').
 */
export const getIsoDate = (value) => {
    return dayjs(value).utc(true).toISOString().split("T")[0];
};
/**
 * Checks if the two given intervals are overlapping each other. Adjacent intervals do not count as overlapping unless `inclusive` option is set to `true`.
 * Logic taken from https://github.com/date-fns/date-fns/blob/main/src/areIntervalsOverlapping/index.ts.
 * @returns Whether the time intervals are overlapping or not.
 */
export const areIntervalsOverlapping = (intervalLeft, intervalRight, options) => {
    const [leftStartTime, leftEndTime] = [
        dayjs(intervalLeft.start).toDate(),
        dayjs(intervalLeft.end).toDate(),
    ].sort((a, b) => a.getTime() - b.getTime());
    const [rightStartTime, rightEndTime] = [
        dayjs(intervalRight.start).toDate(),
        dayjs(intervalRight.end).toDate(),
    ].sort((a, b) => a.getTime() - b.getTime());
    if (options?.inclusive)
        return leftStartTime <= rightEndTime && rightStartTime <= leftEndTime;
    return leftStartTime < rightEndTime && rightStartTime < leftEndTime;
};
/**
 * Formats a time zone identifier into a human-readable name.
 * * This helper uses the `Intl.DateTimeFormat` API with the `en-AU` locale to
 * extract the specific time zone name part (e.g., "AEST" or "Australian Eastern Standard Time").
 *
 * @param {string} timezone - The IANA time zone identifier (e.g., "Australia/Sydney").
 * @param {Intl.DateTimeFormatOptions["timeZoneName"]} [timezoneFormat="short"] - The style of the
 * time zone name. Options include "short", "long", "shortOffset", "longOffset", etc.
 * @returns {string} The localized time zone name, or the original `timezone` string if
 * formatting fails or the part is not found.
 * @example
 * getTimeZoneName("Australia/Sydney", "long"); // "Australian Eastern Standard Time"
 * getTimeZoneName("Australia/Sydney", "short"); // "AEST"
 */
export const getTimeZoneName = (timezone, timezoneFormat = "short") => {
    try {
        return (new Intl.DateTimeFormat("en-AU", {
            timeZone: timezone,
            timeZoneName: timezoneFormat,
        })
            .formatToParts()
            .find((part) => part.type === "timeZoneName")?.value || timezone);
    }
    catch {
        return timezone;
    }
};
export const formatDateForPdf = (value, inputType) => {
    const dateFormat = DATE_FORMAT_MAP[inputType];
    if (!value || !dateFormat) {
        return value;
    }
    const formattedDate = dateFormat.parse
        ? dayjs(value, dateFormat.parse)
        : dayjs(value);
    return formattedDate.isValid()
        ? formattedDate.format(dateFormat.display)
        : value;
};
//# sourceMappingURL=date.js.map